import type { CircularProgressProps, SxProps, Theme } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React from "react";
import { lxStyled } from "../Themes";
import { lxSwitch } from "../Utils";

const Container = lxStyled("div")({
  height: "100%",
  width: "100%",
  display: "grid",
  placeContent: "center",
});

export type LxCircularProgressVariant = "small" | "medium" | "large";

export type LxCircularProgressProps = Omit<CircularProgressProps, "size"> & {
  /**
   * The sx property of the circular progress element.
   */
  circularProgressSx?: SxProps<Theme>;

  /**
   * The variant of the loading indicator:  `small`, `medium`, `large`. Default is `medium`.
   */
  size?: LxCircularProgressVariant;
};

/**
 * LxCircularProgress displays a simple circular loading indicator.
 *
 * By default, the LxCircularProgress is displayed in a
 * container with `height: "100%"`,  and `width: "100%"`.
 * The loading indicator itself is centered in the container.
 *
 * The property `size` can be one of `small`,
 * `medium`, or `large`.
 *
 * - use `small` for loading indicators in small containers, e.g. within a `Button`.
 * - use `medium` for loading indicators in `LxSections`, `LxWidget`, etc...
 * - use `large` for loading indicators in center of pages or large empty spaces.
 *
 * You may use the `sx` property to style the container and use
 * the `circularProgressSx` property to style the loading indicator.
 *
 * @package Components
 *
 * @example
 * function MyComponent() {
 *    if(isLoading) {
 *      return <LxCircularProgress size="large"/>
 *    }
 * }
 */
export function LxCircularProgress(props: LxCircularProgressProps) {
  const { sx, size = "medium", ...otherCircularProgressProps } = props;
  const s = useLxCircularProgressVariant(size);

  return (
    <Container sx={sx} data-testid="lx-loading-indicator">
      <CircularProgress
        {...otherCircularProgressProps}
        sx={props?.circularProgressSx}
        size={s}
      />
    </Container>
  );
}

/**
 * useLxCircularProgressSize() returns the size
 * which should be used with `CircularProgress`.
 *
 * @package Components
 * @deprecated Use `LxLxCircularProgress` instead.
 *
 * @example
 * function MyComponent() {
 *    const size = useLxCircularProgressSize("large");
 *    return <CircularProgress size={size}/>
 * }
 */
export function useLxCircularProgressVariant(
  variant: LxCircularProgressVariant
) {
  return React.useMemo(() => {
    const medium = 10 * 4; // 40 is the default size of the MUI CircularProgress
    return lxSwitch(variant)
      .case("large", 20 * 4)
      .case("medium", medium)
      .case("small", 5 * 4)
      .default(medium)
      .exhaustive();
  }, [variant]);
}
